import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, ErrorHandler } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { AppError } from 'src/app/shared/models/app-errors';

// Matched (lower-cased) against the event as the browser sees it: exception type/value plus the
// *minified* frame function names and chunk file names. The bundler mangles class names, so match on
// messages or bare method names, never on symbolicated "Class.method" pairs.
const IGNORED_SENTRY_ERROR_PATTERNS = [
  'window.webkit.messagehandlers',
  'java object is gone',
  'setcontactautofillvaluesfrombridge',
  'enabledidusertypeonkeyboardlogging',
  // angular-oauth2-oidc session-check timer racing a detached iframe
  'checksession',
  // JSON-RPC error thrown by injected browser-wallet extensions
  'method not found',
];

// A lazy route chunk could not be fetched or parsed. Almost always a stale index.html referencing
// hashed chunks that no longer exist after a deploy (WebKit, Chromium and webpack wordings).
const CHUNK_LOAD_ERROR_PATTERN =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk [\w-]+ failed|ChunkLoadError/i;
const CHUNK_RELOAD_STORAGE_KEY = 'bueno.chunkReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 60 * 1000;

// MatIconRegistry reports failed SVG fetches through the ErrorHandler. Status 0 means the request never
// got a response (navigation away, offline, tab closed), which is noise rather than an app defect.
const ABORTED_ICON_FETCH_PATTERN = /^Error retrieving icon .*Http failure response for .*: 0 /;

function collectSentryEventText(event: any): string {
  const parts: string[] = [];

  if (typeof event?.message === 'string') {
    parts.push(event.message);
  }

  if (typeof event?.logentry?.formatted === 'string') {
    parts.push(event.logentry.formatted);
  }

  const exceptionValues = event?.exception?.values;
  if (Array.isArray(exceptionValues)) {
    exceptionValues.forEach((value: any) => {
      if (typeof value?.type === 'string') {
        parts.push(value.type);
      }

      if (typeof value?.value === 'string') {
        parts.push(value.value);
      }

      const frames = value?.stacktrace?.frames;
      if (Array.isArray(frames)) {
        frames.forEach((frame: any) => {
          if (typeof frame?.function === 'string') {
            parts.push(frame.function);
          }

          if (typeof frame?.filename === 'string') {
            parts.push(frame.filename);
          }
        });
      }
    });
  }

  return parts.join(' ').toLowerCase();
}

export function shouldIgnoreSentryEvent(event: any): boolean {
  const eventText = collectSentryEventText(event);

  return IGNORED_SENTRY_ERROR_PATTERNS.some(pattern => eventText.includes(pattern));
}

@Injectable({
  providedIn: 'root',
})
export class CustomErrorHandler implements ErrorHandler {
  constructor() {}

  handleError(error: unknown): void {
    if (error instanceof AppError && error.isHandled) {
      return;
    }

    const message = this.getErrorMessage(error);

    if (CHUNK_LOAD_ERROR_PATTERN.test(message)) {
      this.handleChunkLoadError(error, message);
      return;
    }

    if (ABORTED_ICON_FETCH_PATTERN.test(message)) {
      return;
    }

    if (error instanceof HttpErrorResponse) {
      if (this.shouldIgnoreHttpError(error)) {
        return;
      }

      Sentry.withScope(scope => {
        scope.setTag('error.kind', 'http');
        scope.setContext('http', {
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? '',
          ok: error.ok,
        });
        Sentry.captureException(new Error(error.message || `HTTP ${error.status} ${error.statusText}`));
      });
      return;
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    const fallbackMessage =
      typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'type' in error && typeof error.type === 'string'
          ? error.type
          : 'Unknown application error';

    Sentry.withScope(scope => {
      if (typeof error === 'object' && error !== null) {
        scope.setContext('non_error_exception', {
          serialized: this.serializeForSentry(error),
        });
      }

      Sentry.captureException(new Error(fallbackMessage));
    });
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'object' && error !== null) {
      const message = (error as { message?: unknown }).message;
      return typeof message === 'string' ? message : '';
    }

    return '';
  }

  /**
   * Reload once so the browser picks up the current index.html and chunk set. If the failure repeats
   * within the cooldown the chunk is genuinely unreachable, so report it instead of looping.
   */
  private handleChunkLoadError(error: unknown, message: string): void {
    if (this.tryReloadForStaleChunk()) {
      return;
    }

    Sentry.withScope(scope => {
      scope.setTag('error.kind', 'chunk_load');
      Sentry.captureException(error instanceof Error ? error : new Error(message));
    });
  }

  private tryReloadForStaleChunk(): boolean {
    try {
      const now = Date.now();
      const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) ?? 0);
      if (lastReloadAt && now - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
      }

      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
      window.location.reload();
      return true;
    } catch {
      return false;
    }
  }

  private serializeForSentry(error: object): string {
    try {
      return JSON.stringify(error);
    } catch {
      return '[unserializable exception]';
    }
  }

  private shouldIgnoreHttpError(error: HttpErrorResponse): boolean {
    return error.status === 0 && (error.url ?? '').includes('/assets/i18n_translations/');
  }
}
